import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { TextNode } from 'lexical';
import { useEffect, useMemo } from 'react';
import {
  displayPathKey,
  FieldRefTarget,
  isFormulaRecord,
  KPI_PERIOD_TARGETS,
  KPI_SECTION_TARGETS,
  normalizeDisplaySegment,
} from '../../util/formula-vocabulary';
import { $createFieldChipNode, fieldChipVariant } from './FieldChipNode';
import {
  EnclosingCall,
  findEnclosingCalls,
  isKpiPeriodSlot,
  isKpiScenarioSlot,
  listsInScope,
  memberSlotList,
} from './enclosingCalls';
import { $displayOffsetOf, $serializeFormula } from './serialization';
import { isInsideStringLiteral } from './StringLiteralPlugin';

export interface RefLookups {
  /** Canonical and legacy paths alike; a canonical path wins a collision. */
  byPath: Map<string, FieldRefTarget>;
  /** Display-path key (see displayPathKey) → entries. */
  byDisplayPath: Map<string, FieldRefTarget[]>;
  /** Lowercased normalized display name → entries. */
  byLowerName: Map<string, FieldRefTarget[]>;
}

export function buildRefLookups(fields: FieldRefTarget[]): RefLookups {
  const byPath = new Map<string, FieldRefTarget>();
  const byDisplayPath = new Map<string, FieldRefTarget[]>();
  const byLowerName = new Map<string, FieldRefTarget[]>();
  for (const field of fields) {
    if (field.legacyPath !== undefined) {
      byPath.set(field.legacyPath, field);
    }
    const displayPath = displayPathKey(field.displayPath);
    byDisplayPath.set(displayPath, [...(byDisplayPath.get(displayPath) ?? []), field]);
    const name = normalizeDisplaySegment(field.displayName).toLowerCase();
    byLowerName.set(name, [...(byLowerName.get(name) ?? []), field]);
  }
  for (const field of fields) {
    byPath.set(field.path, field);
  }
  return { byPath, byDisplayPath, byLowerName };
}

/**
 * Path-first (a path stored before records included), then a dotted ref as a
 * display path, then a single name as a display name with the same scope
 * rule as the modal's validator:
 * inside REDUCE(…, [L], LAMBDA(…)) — `lists`, innermost first — a name means
 * L's member first, then a row/company field. A name that only exists as a
 * member chips as that member wherever it is typed; validation says where it
 * may be used. Ambiguous names, and records (never a value), stay text so
 * validation can explain.
 */
export function resolveRef(
  ref: string,
  lookups: RefLookups,
  lists: string[] = []
): FieldRefTarget | undefined {
  const trimmed = ref.trim();
  const asValue = (target: FieldRefTarget | undefined) =>
    target && !isFormulaRecord(target) ? target : undefined;
  const byPath = lookups.byPath.get(trimmed);
  if (byPath) {
    return asValue(byPath);
  }
  const pathKey = displayPathKey(trimmed);
  if (pathKey.includes('.')) {
    const byDisplayPath = lookups.byDisplayPath.get(pathKey) ?? [];
    return byDisplayPath.length === 1 ? asValue(byDisplayPath[0]) : undefined;
  }
  const candidates = lookups.byLowerName.get(normalizeDisplaySegment(trimmed).toLowerCase()) ?? [];
  for (const list of lists) {
    const member = candidates.find((candidate) => candidate.list?.path === list);
    if (member) {
      return member;
    }
  }
  // The validator's order: a row/company field or import first, then a KPI —
  // shadowed by a same-named field, so it only resolves (for validation to
  // teach KPI's placement) when nothing else answers — then a lone member.
  const rowLevel = candidates.filter(
    (candidate) => !candidate.list && !candidate.kpi && !isFormulaRecord(candidate)
  );
  if (rowLevel.length > 0) {
    return rowLevel.length === 1 ? rowLevel[0] : undefined;
  }
  const kpis = candidates.filter((candidate) => candidate.kpi);
  if (kpis.length > 0) {
    return kpis.length === 1 ? kpis[0] : undefined;
  }
  const members = candidates.filter((candidate) => candidate.list);
  return members.length === 1 ? members[0] : undefined;
}

/**
 * In KPI's period/scenario slots a name means one of the enum-like tokens
 * ([Quarter], [Actual]) — matched case-insensitively, like validation
 * canonicalizes them. Undefined outside those slots.
 */
export function resolveKpiTokenRef(ref: string, call: EnclosingCall | undefined): FieldRefTarget | undefined {
  const targets = isKpiPeriodSlot(call)
    ? KPI_PERIOD_TARGETS
    : isKpiScenarioSlot(call)
      ? KPI_SECTION_TARGETS
      : [];
  const lower = ref.trim().toLowerCase();
  return targets.find((target) => target.displayName.toLowerCase() === lower);
}

/**
 * Converts a completed "[Display Name]" (typed by hand or pasted) into a chip
 * the moment it resolves. Transforms re-run on the split-off remainder nodes,
 * so a pasted formula with several refs tokenizes fully; unresolvable refs stay
 * editable text and surface through validation.
 */
export function AutoTokenizePlugin({ fields }: { fields: FieldRefTarget[] }) {
  const [editor] = useLexicalComposerContext();
  const lookups = useMemo(() => buildRefLookups(fields), [fields]);

  useEffect(() => {
    return editor.registerNodeTransform(TextNode, (node) => {
      if (!node.isSimpleText()) {
        return;
      }
      const text = node.getTextContent();
      // One serialization pass per transform; each match reuses it for its scope.
      const display = $serializeFormula().display;
      const nodeOffset = $displayOffsetOf(node, 0);
      const resolveListPath = (ref: string) => {
        const list = resolveRef(ref, lookups);
        return list?.type === 'list' ? list.path : undefined;
      };
      for (const match of text.matchAll(/\[([^\][]*)\]/g)) {
        // "[FMV]" typed inside an open string literal is text, not a ref.
        if (isInsideStringLiteral(text.slice(0, match.index))) {
          continue;
        }
        const calls = findEnclosingCalls(display, nodeOffset + match.index);
        // In INDEX's third argument and SUM's second a name means the call's own list's member first.
        const memberList = memberSlotList(calls[0], resolveListPath);
        const lists = [
          ...(memberList === undefined ? [] : [memberList]),
          ...listsInScope(calls, resolveListPath),
        ];
        const target = resolveKpiTokenRef(match[1], calls[0]) ?? resolveRef(match[1], lookups, lists);
        if (!target) {
          continue;
        }
        const start = match.index;
        const end = start + match[0].length;
        let refNode: TextNode;
        if (start === 0) {
          [refNode] = node.splitText(end);
        } else {
          [, refNode] = node.splitText(start, end);
        }
        refNode.replace($createFieldChipNode(target.path, target.displayPath, fieldChipVariant(target)));
        return;
      }
    });
  }, [editor, lookups]);

  return null;
}
